import { FC, memo, useCallback, useState, useMemo } from "react";
import styles from "./PortfolioBanner.module.css";

import { Col, Row, Form, Space, message } from "antd";
import clsx from "clsx";
import { useMutation, useQueryClient } from "react-query";
import { updatePortfolioThemeRequest, usePortfolio } from "api/portfolio";
import { AxiosError } from "axios";
import { getImageUrl } from "utils/urlParser";
import { useParams } from "react-router-dom";
import { useUser } from "api/auth";
import { ColorPicker } from "components/ColorPicker";
import { ButtonUpload } from "components/ImageUpload";
import { Button } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";

interface EditBannerProps {
  username: string;
  onClose: () => void;
  onPhotoChange: (fileName: string) => void;
  onColorChange: (fileName: string) => void;
}

const EditBanner: FC<EditBannerProps> = ({
  onClose,
  onPhotoChange,
  onColorChange,
  username,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data: initData } = usePortfolio(username, {
    enabled: !!username,
  });
  const { mutate } = useMutation(updatePortfolioThemeRequest, {
    onSuccess: async () => {
      message.success("Your setting have been saved");
      queryClient.invalidateQueries(["portfolio", username]);
    },
    onError: (err: AxiosError, _) => {
      message.error(err.response?.data?.message);
    },
  });

  return (
    <Form
      onFinish={mutate}
      className={clsx(styles.editBanner, "container")}
      form={form}
      initialValues={{
        headerPhotoLink: initData?.theme?.headerPhotoLink,
        headerBackgroundColor: initData?.theme?.headerBackgroundColor,
      }}
    >
      <Row>
        <Col xs={24} lg={10}>
          <h3>Header photo</h3>
          <p>
            Photo will scale to fit, but we recommend at least 1280px×200px,
            JPG, PNG or GIF format, 5 MB max file size
          </p>
          <Form.Item name="headerPhotoLink">
            <ButtonUpload
              initFileName={initData?.theme?.headerPhotoLink}
              onChange={(fileName) => {
                onPhotoChange(fileName);
                form.setFieldsValue({ headerPhotoLink: fileName });
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={10}>
          <h3>Header background color</h3>
          <p>Select a custom color or use one of our suggested options</p>
          <Form.Item name="headerBackgroundColor">
            <ColorPicker
              initValue={initData?.theme?.headerBackgroundColor}
              name="headerBackgroundColor"
              onChange={(hex) => {
                onColorChange(hex);
                form.setFieldsValue({ headerBackgroundColor: hex });
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={4}>
          <Space direction="vertical" align="end">
            <Button type="primary" htmlType="submit">
              Save header design
            </Button>
            <Button color="default" onClick={onClose}>
              Cancel
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

const PortfolioBanner: FC = () => {
  const [showEditBanner, setShowEditBanner] = useState(false);
  const [banner, setBanner] = useState("");
  const [color, setColor] = useState("");
  const { username } = useParams<{ username: string }>();

  const { data } = usePortfolio(username, { enabled: !!username });
  const { data: me } = useUser({ enabled: false });

  const toggleBanner = useCallback(() => {
    setShowEditBanner(!showEditBanner);
    setBanner("");
    setColor("");
  }, [showEditBanner]);

  const handleBannerChange = useCallback((value: string) => {
    setBanner(value);
  }, []);

  const handleColorChange = useCallback((value: string) => {
    setColor(value);
  }, []);

  const isOwner = useMemo(() => {
    return me?._id === data?._id;
  }, [me, data]);

  const headerPhotoLink = useMemo(() => {
    return getImageUrl(banner || data?.theme?.headerPhotoLink);
  }, [banner, data?.theme?.headerPhotoLink]);
  const headerBackgroundColor = useMemo(() => {
    return color || data?.theme?.headerBackgroundColor || "#1d2048";
  }, [color, data?.theme?.headerBackgroundColor]);
  return (
    <div>
      {showEditBanner && isOwner && (
        <EditBanner
          onPhotoChange={handleBannerChange}
          onColorChange={handleColorChange}
          username={username}
          onClose={toggleBanner}
        />
      )}

      <div
        className={styles.root}
        style={{
          backgroundImage: `url(${headerPhotoLink})`,
          backgroundColor: `${headerBackgroundColor}`,
        }}
      >
        {!showEditBanner && isOwner && (
          <div className="relative container h-100">
            <Button
              className={styles.btnEdit}
              onClick={toggleBanner}
              color="default"
            >
              <FontAwesomeIcon className="mr-8" color="#fff" icon={faCamera} />
              Edit header
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PortfolioBanner);
